import { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import MiniHeader from '../../../../UI/MiniHeader'
import Loading from '../../../../UI/Loading'
import CompanyProgress from '../../../../UI/CompanyProgress'

import BasicInfoTab from '../../common/CompanyProfile/components/BasicInfoTab'
import ContactTab from '../../common/CompanyProfile/components/ContactTab'
import SchoolTeachersSection from '../../common/CompanyProfile/components/SchoolTeachersSection'
import BusinessTab from '../../common/CompanyProfile/components/BusinessTab'
import RiskTab from '../../common/CompanyProfile/components/RiskTab'
import PeopleTab, { VIEWS } from '../../common/CompanyProfile/components/PeopleTab'
import ExpertsTab from '../../common/CompanyProfile/components/ExpertsTab'
import ExpertsFlowTab from '../../common/CompanyProfile/components/ExpertsFlowTab'
import DocumentsTab from '../../common/CompanyProfile/components/DocumentsTab'
import SharesTab from '../../common/CompanyProfile/components/SharesTab'
import LogTab from '../../common/CompanyProfile/components/LogTab'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { useAvatarUrls } from '../../../../shared/useAvatarUrls'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import { isDialogOpen } from '../../../../shared/useDialogDismiss'

import styles from '../../common/CompanyProfile/style.module.scss'

/**
 * Unternehmensprofil aus der Sicht der Lehrkraft.
 *
 * Bis 23.08.2026 war das ein Fenster mit drei Abschnitten: Stammdaten,
 * Geschäftstätigkeit, Markt- und Risikoanalyse. Alles daran kam aus der Zeile
 * der Unternehmensliste, weil es für Lehrkräfte keine Abfrage zu einer
 * einzelnen Junior Company gab. Wer die Company begleitet, sah damit **weniger
 * als seine Schüler:innen**: kein Team, keine Nachweise, keine
 * Wirtschaftsexpert:innen, keine Unterlagen, keine Anteile.
 *
 * Jetzt ist es dieselbe Seite wie das Firmenprofil der Verwaltung
 * (`views/admin/CompanyReview`) — dieselben Abschnitte in derselben
 * Reihenfolge, dieselben Bausteine (`views/common/CompanyProfile/components`),
 * derselbe Fortschrittsbalken und dieselben Ampeln am Untermenü. Wer einen
 * Abschnitt beanstandet, nennt in beiden Rollen denselben Namen.
 *
 * **Nur lesend.** Aktivieren, ablehnen, umbenennen, Unterlagen nachtragen,
 * Nachweise prüfen und Lehrkräfte zuweisen bleiben bei der Verwaltung; die
 * Nachweise selbst lädt die Lehrkraft in ihrer Schüler:innenliste hoch
 * (Anforderung #94). Ein Knopf, den der Server ablehnt, wäre schlimmer als
 * keiner.
 */

const TABS = [
	{ key: 'basic', label: 'Basic Information' },
	{ key: 'business', label: 'Business Activity' },
	{ key: 'risk', label: 'Risk Analysis' },
	{ key: 'people', label: 'employees' },
	{ key: 'experts', label: 'Economic experts' },
	{ key: 'expertsFlow', label: 'Quick guide', parent: 'experts' },
	{ key: 'documents', label: 'documents' },
	{ key: 'shares', label: 'Company Shares' },
	// Nicht „Kommentare": Hier stehen nur die Nachrichten, die die Verwaltung
	// ausdrücklich an die Lehrkräfte gerichtet hat. Der interne Verlauf des
	// Unternehmens bleibt in der Verwaltung.
	{ key: 'messages', label: 'Messages from the administration' }
]

/**
 * Zustand des Unternehmens — dieselbe Reihenfolge und dieselben Worte wie im
 * Firmenprofil der Verwaltung: Eine Ablehnung überschreibt alles Weitere,
 * danach zählt die Aktivierung, dann die eingereichte Prüfung.
 */
const companyStatus = companyInfo => {
	if (!companyInfo) return null
	if (companyInfo.isRejected) return { label: 'Company Rejected', tone: 'Red' }
	if (companyInfo.isActive) return { label: 'Company status active', tone: 'Green' }
	if (companyInfo.isNeedReview) return { label: 'Company status in review', tone: 'Amber' }
	return { label: 'Company status not activated', tone: 'Grey' }
}

// Liste oder Kacheln bei den Mitarbeiter:innen — die Wahl soll den nächsten
// Aufruf überleben. Eigener Schlüssel: Die Lehrkraft schaut auf andere Firmen
// als die Verwaltung und darf dort anders eingestellt sein.
const VIEW_STORAGE_KEY = 'teacherCompanyProfile.peopleView'

function TeacherCompanyProfile(props) {
	const { t } = useTranslation()
	const history = useHistory()
	const { id } = useParams()

	const [company, setCompany] = useState(null)
	const [isLoading, setLoading] = useState(true)
	const [hasFailed, setFailed] = useState(false)
	const [activeTab, setActiveTab] = useState(TABS[0].key)
	const [peopleView, setPeopleView] = useState(() => localStorage.getItem(VIEW_STORAGE_KEY) || VIEWS.GRID)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(activeTab, tabsRef)

	const {
		companyInfo,
		employees,
		invitationUser,
		experts,
		supervision,
		teachers,
		businessPlan,
		riskAnalysis,
		companyCertificate,
		finalReport,
		activationsStatus
	} = company || {}

	// Bilder werden angemeldet geholt (siehe shared/useAvatarUrls) — ein <img src>
	// könnte den Anmelde-Kopfeintrag nicht mitschicken.
	const avatarUrls = useAvatarUrls([
		...(employees || []).map(item => item.avatarHashName),
		...(invitationUser || []).map(item => item.avatarHashName),
		...(experts || []).map(item => item.avatarHashName),
		...(teachers || []).map(item => item.Teacher?.avatarHashName),
		supervision?.Teacher?.avatarHashName
	])

	/*
	 * An der Kennung, nicht nur beim ersten Aufbau: Wechselt die Adresse von
	 * einem Profil zum nächsten, bleibt die Komponente stehen — mit `[]` stünden
	 * dann die Stammdaten der vorigen Company neben den Daten der neuen.
	 *
	 * Scheitert die Abfrage (eine nicht betreute Company antwortet mit 404),
	 * sagt die Seite das. Leere Abschnitte sähen aus wie ein Unternehmen ohne
	 * Angaben.
	 */
	useEffect(() => {
		setLoading(true)
		setFailed(false)

		http.get(TApi.TEACHER_GET_COMPANY_PROFILE(id))
			.then(({ data }) => setCompany(data))
			.catch(() => setFailed(true)) // Meldung kommt aus shared/http
			.finally(() => setLoading(false))
	}, [id])

	useEffect(() => {
		const handleEsc = e => {
			if (e.key !== 'Escape') return
			const tag = document.activeElement?.tagName
			if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
			if (document.querySelector('.swal2-container')) return
			// Ein offenes Fenster hat Vorrang: Es schliesst sich selbst, die
			// Seite darunter bleibt stehen.
			if (isDialogOpen()) return
			history.goBack()
		}
		document.addEventListener('keydown', handleEsc)
		return () => document.removeEventListener('keydown', handleEsc)
	}, [history])

	const handleChangeView = view => {
		setPeopleView(view)
		localStorage.setItem(VIEW_STORAGE_KEY, view)
	}

	/*
	 * Dieselbe Rechnung wie im Firmenprofil der Verwaltung: Was an einem
	 * Abschnitt noch fehlt, steht als Ampel am Untermenü. Die Lehrkraft ist es,
	 * die dem hinterherläuft — sie soll nicht jeden Abschnitt einzeln öffnen
	 * müssen, um zu sehen, wo es hakt.
	 */
	const activations = {
		employees: employees?.length >= 2 && employees.every(employee => !!employee.reviewedParentalConfirmation),
		// Wie der Server zaehlt: verbundene Unterstuetzer:innen plus Altbestand
		// (countAssignedExperts). `experts` ist nur der Altbestand — den gibt es
		// auf Prod nicht mehr, und der Knopf blieb bei jeder Company grau (#193).
		experts: activationsStatus ? !!activationsStatus.experts : experts?.length > 0,
		supervision: !!supervision,
		contacts: !!(companyInfo?.email && companyInfo?.phone),
		school: !!supervision?.Teacher?.School,
		businessActivity: !!(companyInfo?.description && companyInfo?.offerTypes),
		businessIdea: !!companyInfo?.businessIdea,
		documents: !!riskAnalysis
	}

	const tabStatus = {
		basic: activations.contacts && activations.supervision && activations.school,
		business: activations.businessActivity && activations.businessIdea,
		risk: activations.documents,
		people: activations.employees,
		experts: activations.experts,
		// Reine Erklärung bzw. nichts abzuhaken.
		expertsFlow: null,
		documents: null,
		shares: null,
		messages: null
	}

	const renderTab = () => {
		switch (activeTab) {
			case 'business':
				return <BusinessTab companyInfo={companyInfo} typeOfOffers={props.typeOfOffers} branch={props.branch} />
			case 'risk':
				return <RiskTab riskAnalysis={riskAnalysis} />
			case 'people':
				return (
					<PeopleTab
						readOnly
						companyInfo={companyInfo}
						employees={employees}
						invitationUser={invitationUser}
						avatarUrls={avatarUrls}
						workAreas={props.workAreas}
						view={peopleView}
						onChangeView={handleChangeView}
					/>
				)
			case 'experts':
				return (
					<ExpertsTab
						companyId={id}
						experts={experts}
						avatarUrls={avatarUrls}
						supportersUrl={TApi.TEACHER_GET_COMPANY_SUPPORTERS}
					/>
				)
			case 'expertsFlow':
				return <ExpertsFlowTab />
			case 'documents':
				return (
					<DocumentsTab
						readOnly
						companyId={id}
						companyInfo={companyInfo}
						businessPlan={businessPlan}
						companyCertificate={companyCertificate}
						finalReport={finalReport}
					/>
				)
			case 'shares':
				return (
					<SharesTab
						companyId={id}
						listUrl={TApi.TEACHER_GET_COMPANY_SHAREHOLDERS}
						downloadUrl={TApi.GET_FILES_DOCUMENT}
					/>
				)
			case 'messages':
				return <LogTab readOnly companyId={id} url={TApi.COMMON_COMPANY_LOG} />
			default:
				return (
					<div className={styles.stack}>
						<BasicInfoTab readOnly companyInfo={companyInfo} />
						<ContactTab readOnly companyInfo={companyInfo} />
						<SchoolTeachersSection
							readOnly
							companyId={id}
							supervision={supervision}
							teachers={teachers}
							avatarUrls={avatarUrls}
						/>
					</div>
				)
		}
	}

	const status = companyStatus(companyInfo)

	return (
		<div className={styles.root}>
			{/*
			 * „Unternehmensprofil" und nicht „Firmenprofil" wie bei der
			 * Verwaltung: So heisst es an der Schule — im Menue steht
			 * „Unternehmen", und die Schueler:innen sehen dasselbe Wort ueber
			 * ihrem eigenen Profil.
			 */}
			<MiniHeader
				className={styles.header}
				title={companyInfo?.name ? `${t('companyProfile')} — ${companyInfo.name}` : t('companyProfile')}
				bg="#F8FAFF"
			/>

			{hasFailed ? (
				<div className={styles.emptyPanel}>{t('The company profile could not be loaded.')}</div>
			) : isLoading ? (
				<Loading />
			) : (
				<>
					{/* Wo steht diese Junior Company im Programm? Derselbe Balken,
					    den die Lehrkraft auf ihrem Dashboard sieht. */}
					{!!companyInfo && (
						<CompanyProgress
							compact
							className={styles.progress}
							companyType={companyInfo.companyType}
							activationsStatus={activationsStatus}
							isActivated={!!companyInfo.isActive}
							isNeedReview={!!companyInfo.isNeedReview}
						/>
					)}

					<div className={styles.layout}>
						<nav className={styles.nav} ref={tabsRef} aria-label={t('companyProfile')}>
							{status && (
								<div className={styles.statusCard}>
									<span className={styles.statusCardLabel}>{t('Status')}</span>
									<span className={clsx(styles.statusPill, styles[`statusPill${status.tone}`])}>
										{t(status.label)}
									</span>
								</div>
							)}

							{TABS.map(tab => {
								const done = tabStatus[tab.key]
								const isActive = activeTab === tab.key
								return (
									<button
										key={tab.key}
										type="button"
										aria-current={isActive ? 'true' : undefined}
										className={clsx(
											styles.navItem,
											tab.parent && styles.navItemChild,
											isActive && styles.navItemActive
										)}
										onClick={() => setActiveTab(tab.key)}
									>
										{done !== null ? (
											<span
												className={clsx(
													styles.tabDot,
													done ? styles.tabDotDone : styles.tabDotOpen
												)}
												title={done ? t('Complete') : t('Incomplete')}
											/>
										) : (
											<span className={styles.tabDotPlaceholder} />
										)}
										{t(tab.label)}
									</button>
								)
							})}
						</nav>

						<div className={styles.content}>
							<div className={styles.panel}>{renderTab()}</div>
						</div>
					</div>
				</>
			)}
		</div>
	)
}

TeacherCompanyProfile.propTypes = {
	workAreas: PropTypes.array,
	typeOfOffers: PropTypes.array,
	branch: PropTypes.array
}

const mapStateToProps = state => ({
	workAreas: state.options.workAreas,
	typeOfOffers: state.options.typeOfOffers,
	branch: state.options.branch
})

export default connect(mapStateToProps)(TeacherCompanyProfile)
